/*
 * The module's routes (S13 §1): `/ai` (the start page), `/ai/new` (the new-chat form on a page of its
 * own), `/ai/$key` (a chat), `/ai/$key/changes` and `/ai/$key/changes/$repo/$` (its review, the file as
 * a splat), and the module's own dead end for every other address under `/ai`. They are created against
 * the shell's layout route, which arrives as the argument of `FrontendModule.routes`: a module never
 * imports the router tree it is mounted into (modules.md §8).
 *
 * The list resolves at once and each screen behind it is a `lazy()`, because this file is statically
 * reachable from `main.tsx`, so what may stand in it is `createRoute` and an `import()` named by a
 * literal specifier (`core/modules/entryClosure.test.ts`, `scripts/checkBudgets.ts`).
 *
 * `new` can never be a key — a key holds a digit and a letter of the look-alike-free alphabet — and a
 * static segment outranks a parameter anyway. A key is checked against the generated pattern before
 * anything is read: a malformed one is the not-found page without a request, the same page an unknown
 * key and a chat the reader does not own end on.
 */

import { createRoute, useRouterState, type AnyRoute } from "@tanstack/react-router"
import { lazy } from "react"
import { useShortcutScope } from "../../core/shell/index.ts"
import { AI_PLACE } from "../../routes/links.ts"
import { KeyPattern } from "./wire/aichat.gen.ts"

const HomeScreen = lazy(async () => ({
	default: (await import("./home/AiHome.tsx")).AiHome,
}))

const NewChatScreen = lazy(async () => ({
	default: (await import("./new/NewChatPage.tsx")).NewChatPage,
}))

const ChatScreen = lazy(async () => ({
	default: (await import("./chat/ChatPage.tsx")).ChatPage,
}))

const ReviewScreen = lazy(async () => ({
	default: (await import("./review/ReviewPage.tsx")).ReviewPage,
}))

const NotFoundScreen = lazy(async () => ({
	default: (await import("./home/AiNotFound.tsx")).AiNotFound,
}))

const KEY = new RegExp(KeyPattern)

/** Whether an address segment can be a chat's key at all. */
export function isChatKey(segment: string): boolean {
	return KEY.test(segment)
}

/**
 * The key in an address under `/ai/`: its second segment. The page reads it from the location, so the
 * mapping from a URL to a chat is a pure function with a test rather than a shape spread over the
 * router's parameter types.
 */
export function keyOfPath(pathname: string): string {
	return decodeURIComponent(pathname.split("/")[2] ?? "")
}

/** The key of the address on screen. */
function useChatKey(): string {
	return useRouterState({ select: (state) => keyOfPath(state.location.pathname) })
}

/**
 * A chat, with the `aichat` shortcut scope open for as long as it is on route (S12 §9). The scope is
 * pushed here and not in the page for chat's reason (`modules/chat/routes.tsx`): the page is rendered
 * bare in its unit tests, and a chat still opening is a chat page all the same.
 */
function ChatRoute() {
	useShortcutScope("aichat")
	const key = useChatKey()
	return isChatKey(key) ? <ChatScreen chatKey={key} /> : <NotFoundScreen />
}

/** A chat's review, keyed the same way. */
function ReviewRoute() {
	const key = useChatKey()
	return isChatKey(key) ? <ReviewScreen chatKey={key} /> : <NotFoundScreen />
}

/** The module's routes, built against the parent the shell hands in. */
export function aichatRoutes(parent: AnyRoute): AnyRoute[] {
	const deadEnd = { notFoundComponent: NotFoundScreen }
	return [
		createRoute({
			getParentRoute: () => parent,
			path: AI_PLACE,
			component: HomeScreen,
			...deadEnd,
		}),
		createRoute({
			getParentRoute: () => parent,
			path: `${AI_PLACE}/new`,
			component: NewChatScreen,
			...deadEnd,
		}),
		createRoute({
			getParentRoute: () => parent,
			path: `${AI_PLACE}/$key`,
			component: ChatRoute,
			...deadEnd,
		}),
		createRoute({
			getParentRoute: () => parent,
			path: `${AI_PLACE}/$key/changes`,
			component: ReviewRoute,
			...deadEnd,
		}),
		createRoute({
			getParentRoute: () => parent,
			path: `${AI_PLACE}/$key/changes/$repo/$`,
			component: ReviewRoute,
			...deadEnd,
		}),
		// One segment below a chat that no route above claims: `/ai/<key>/nonsense`, an old link. It
		// is two parameters and not a splat: TanStack ranks `/ai/$` above `/ai` itself (a splat also
		// matches an empty rest), which drew the dead end on the start page.
		createRoute({
			getParentRoute: () => parent,
			path: `${AI_PLACE}/$key/$rest`,
			component: NotFoundScreen,
		}),
	] as AnyRoute[]
}
